/**
 * Form estruturado pra criar/editar CatalogItem.
 *
 * OpenSpec add-categorized-catalog-guideline (PR 2 Fase 3).
 *
 * UX: form vertical com seções colapsáveis (Identificação, Descrição,
 * Atributos, FAQ, Integrações). Validação inline básica; backend faz
 * validação completa + guardrails.
 */
import React, { useEffect, useMemo, useState } from 'react';
import type {
  CatalogItem,
  CatalogItemStatus,
  CatalogItemType,
  CatalogFaqEntry,
  CreateCatalogItemRequest,
} from '../../types';

interface Props {
  /** undefined = criar; defined = editar item existente. */
  initial?: CatalogItem;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (payload: CreateCatalogItemRequest) => Promise<void>;
}

interface AttributeRow {
  key: string;
  value: string;
}

function attributesToRows(attrs: Record<string, unknown>): AttributeRow[] {
  return Object.entries(attrs).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
}

function rowsToAttributes(rows: AttributeRow[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    if (row.key.trim()) {
      out[row.key.trim()] = row.value;
    }
  }
  return out;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section
    style={{
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: 16,
      marginBottom: 14,
      background: '#fff',
    }}
  >
    <h4 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700, color: '#374151' }}>
      {title}
    </h4>
    {children}
  </section>
);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 14,
  background: '#fff',
  fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: '#475569',
  marginBottom: 4,
  fontWeight: 600,
};

const buttonPrimary: React.CSSProperties = {
  padding: '10px 18px',
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
const buttonSecondary: React.CSSProperties = {
  padding: '10px 18px',
  background: '#fff',
  color: '#475569',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
const buttonGhost: React.CSSProperties = {
  padding: '4px 10px',
  background: 'transparent',
  color: '#b91c1c',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
};

export const CatalogItemForm: React.FC<Props> = ({ initial, saving, onCancel, onSubmit }) => {
  const [name, setName] = useState(initial?.name || '');
  const [type, setType] = useState<CatalogItemType>(initial?.type || 'servico');
  const [categoryId, setCategoryId] = useState(initial?.categoryId || '');
  const [categoryLabel, setCategoryLabel] = useState(initial?.categoryLabel || '');
  const [status, setStatus] = useState<CatalogItemStatus>(initial?.status || 'active');
  const [descriptionShort, setDescriptionShort] = useState(initial?.descriptionShort || '');
  const [descriptionLong, setDescriptionLong] = useState(initial?.descriptionLong || '');
  const [attributeRows, setAttributeRows] = useState<AttributeRow[]>(
    initial ? attributesToRows(initial.attributes) : [],
  );
  const [faq, setFaq] = useState<CatalogFaqEntry[]>(initial?.faq || []);
  const [integrationsInput, setIntegrationsInput] = useState(
    (initial?.integrations || []).join(', '),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Auto-derive categoryId slug do categoryLabel quando criando novo item
    if (!initial && categoryLabel && !categoryId) {
      const slug = categoryLabel
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
      setCategoryId(slug);
    }
  }, [categoryLabel, categoryId, initial]);

  const integrations = useMemo(
    () =>
      integrationsInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [integrationsInput],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }
    if (!categoryId.trim() || !categoryLabel.trim()) {
      setError('Categoria é obrigatória (label + slug auto-gerado)');
      return;
    }

    const payload: CreateCatalogItemRequest = {
      name: name.trim(),
      type,
      categoryId: categoryId.trim(),
      categoryLabel: categoryLabel.trim(),
      status,
      descriptionShort: descriptionShort.trim() || undefined,
      descriptionLong: descriptionLong.trim() || undefined,
      attributes: rowsToAttributes(attributeRows),
      faq: faq.filter((f) => f.question.trim() && f.answer.trim()),
      integrations,
    };

    try {
      await onSubmit(payload);
    } catch (err) {
      setError((err as Error).message || 'Erro ao salvar');
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Section title="Identificação">
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Nome*</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Curso de Desenvolvimento Web"
              style={inputStyle}
              maxLength={200}
              required
            />
          </div>
          <div>
            <label style={labelStyle}>Tipo*</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as CatalogItemType)}
              style={inputStyle}
            >
              <option value="servico">Serviço</option>
              <option value="produto">Produto</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Categoria (label)*</label>
            <input
              type="text"
              value={categoryLabel}
              onChange={(e) => setCategoryLabel(e.target.value)}
              placeholder="Ex: Tecnologia"
              style={inputStyle}
              maxLength={100}
            />
          </div>
          <div>
            <label style={labelStyle}>Categoria (slug)*</label>
            <input
              type="text"
              value={categoryId}
              onChange={(e) =>
                setCategoryId(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_-]/g, '_'),
                )
              }
              placeholder="auto"
              style={inputStyle}
              maxLength={80}
            />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as CatalogItemStatus)}
              style={inputStyle}
            >
              <option value="active">Ativo</option>
              <option value="draft">Rascunho</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
        </div>
      </Section>

      <Section title="Descrição">
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Descrição curta (até 280 chars)</label>
          <input
            type="text"
            value={descriptionShort}
            onChange={(e) => setDescriptionShort(e.target.value)}
            placeholder="Resumo de 1 linha — aparece em listas"
            style={inputStyle}
            maxLength={280}
          />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            {descriptionShort.length}/280
          </div>
        </div>
        <div>
          <label style={labelStyle}>Descrição longa (Markdown)</label>
          <textarea
            value={descriptionLong}
            onChange={(e) => setDescriptionLong(e.target.value)}
            placeholder="Detalhes completos do produto/serviço..."
            style={{ ...inputStyle, minHeight: 120, fontFamily: 'monospace', fontSize: 13 }}
            maxLength={8000}
          />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            {descriptionLong.length}/8000
          </div>
        </div>
      </Section>

      <Section title="Atributos">
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
          Key-value pairs (modalidade=EAD, duração=4 semestres, preço=R$ 116/mês, etc).
          Backend separa atributos de preço em chunk dedicado por palavras-chave
          (preco/valor/parcelamento/desconto/mensalidade).
        </div>
        {attributeRows.map((row, idx) => (
          <div
            key={idx}
            style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginBottom: 6 }}
          >
            <input
              type="text"
              value={row.key}
              onChange={(e) => {
                const next = [...attributeRows];
                next[idx] = { ...next[idx], key: e.target.value };
                setAttributeRows(next);
              }}
              placeholder="chave (ex: modalidade)"
              style={inputStyle}
            />
            <input
              type="text"
              value={row.value}
              onChange={(e) => {
                const next = [...attributeRows];
                next[idx] = { ...next[idx], value: e.target.value };
                setAttributeRows(next);
              }}
              placeholder="valor (ex: EAD)"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => setAttributeRows(attributeRows.filter((_, i) => i !== idx))}
              style={buttonGhost}
              title="Remover"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setAttributeRows([...attributeRows, { key: '', value: '' }])}
          style={buttonSecondary}
        >
          + Adicionar atributo
        </button>
      </Section>

      <Section title="FAQs (opcional)">
        {faq.map((entry, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              marginBottom: 12,
              padding: 12,
              background: '#f8fafc',
              borderRadius: 6,
            }}
          >
            <input
              type="text"
              value={entry.question}
              onChange={(e) => {
                const next = [...faq];
                next[idx] = { ...next[idx], question: e.target.value };
                setFaq(next);
              }}
              placeholder="Pergunta"
              style={inputStyle}
              maxLength={200}
            />
            <textarea
              value={entry.answer}
              onChange={(e) => {
                const next = [...faq];
                next[idx] = { ...next[idx], answer: e.target.value };
                setFaq(next);
              }}
              placeholder="Resposta"
              style={{ ...inputStyle, minHeight: 60 }}
              maxLength={2000}
            />
            <button
              type="button"
              onClick={() => setFaq(faq.filter((_, i) => i !== idx))}
              style={{ ...buttonGhost, alignSelf: 'flex-end' }}
            >
              Remover pergunta
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setFaq([...faq, { question: '', answer: '' }])}
          style={buttonSecondary}
        >
          + Adicionar pergunta
        </button>
      </Section>

      <Section title="Integrações / Tags (opcional)">
        <label style={labelStyle}>Tags separadas por vírgula</label>
        <input
          type="text"
          value={integrationsInput}
          onChange={(e) => setIntegrationsInput(e.target.value)}
          placeholder="Anhanguera, EAD, Tecnólogo"
          style={inputStyle}
        />
        {integrations.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {integrations.map((tag, idx) => (
              <span
                key={idx}
                style={{
                  background: '#eef2ff',
                  color: '#4338ca',
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </Section>

      {error && (
        <div
          style={{
            padding: 10,
            background: '#fef2f2',
            color: '#b91c1c',
            border: '1px solid #fecaca',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" onClick={onCancel} style={buttonSecondary} disabled={saving}>
          Cancelar
        </button>
        <button type="submit" style={{ ...buttonPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving}>
          {saving ? 'Salvando…' : initial ? 'Salvar alterações' : 'Salvar e indexar'}
        </button>
      </div>
    </form>
  );
};

export default CatalogItemForm;
